import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, MapPin, Clock, Download } from "lucide-react";
import { CLINIC, NAV, IMAGES } from "@/data/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="hidden md:block bg-[#2A2521] text-[#DCC79A] text-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-10">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {CLINIC.addressShort}</span>
            <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Luni–Vineri 08:00–17:00</span>
          </div>
          <div className="flex items-center gap-5">
            <a href={CLINIC.resultsUrl} target="_blank" rel="noopener noreferrer" data-testid="topbar-results"
              className="flex items-center gap-2 font-semibold hover:text-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Rezultate online
            </a>
            <a href={CLINIC.phoneRaw} data-testid="topbar-phone" className="flex items-center gap-2 font-semibold hover:text-white transition-colors">
              <Phone className="w-3.5 h-3.5" /> {CLINIC.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-[#FBF7EF]/95 backdrop-blur-md shadow-[0_4px_24px_rgba(166,130,59,0.12)]" : "champagne-gradient"}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 lg:h-24">
          <Link to="/" data-testid="header-brand-logo" className="flex items-center gap-3 group shrink-0">
            <span className="brand-serif text-3xl lg:text-4xl gold-text-gradient pr-[0.35em] inline-block group-hover:scale-105 transition-transform">MEDAS</span>
            <span className="hidden xl:flex flex-col items-start leading-none border-l border-[#A6823B]/25 pl-3">
              <span className="brand-serif text-base lg:text-lg italic text-[#8A6B2E]">by Excellab</span>
              <span className="text-[9px] lg:text-[10px] uppercase tracking-[0.26em] text-[#8A6B2E]/80 mt-1.5">Clinică & Laborator</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.to === "/" ? "acasa" : item.to.slice(1)}`}
                className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${isActive ? "text-[#A6823B] bg-white/60" : "text-[#4A3F2E] hover:text-[#A6823B] hover:bg-white/50"}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <a href={CLINIC.phoneRaw} data-testid="header-phone-cta"
              className="hidden sm:flex items-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold px-4 lg:px-5 py-2.5 rounded-full transition-colors shadow-lg shadow-[#A6823B]/25 whitespace-nowrap">
              <Phone className="w-4 h-4 shrink-0" /> <span className="hidden xl:inline">Programează-te</span> {CLINIC.phone}
            </a>
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-[#A6823B]" aria-label="Meniu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#EADFC6] bg-[#FBF7EF]" data-testid="mobile-menu">
            <nav className="flex flex-col px-6 py-4">
              {NAV.map((item) => (
                <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                  className="py-3 text-[#4A3F2E] font-medium border-b border-[#EADFC6]/60 last:border-0">
                  {item.label}
                </Link>
              ))}
              <a href={CLINIC.resultsUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
                className="mt-3 flex items-center justify-center gap-2 border border-[#A6823B] text-[#A6823B] font-semibold py-3 rounded-full">
                <Download className="w-4 h-4" /> Rezultate online
              </a>
              <a href={CLINIC.phoneRaw} className="mt-3 flex items-center justify-center gap-2 bg-[#A6823B] text-white font-semibold py-3 rounded-full">
                <Phone className="w-4 h-4" /> Sună: {CLINIC.phone}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
